/* eslint-disable @typescript-eslint/no-empty-function */
import { useState } from 'react';

import {
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
} from '@heroicons/react/20/solid';
import Cookies from 'js-cookie';
import jwt, { JwtPayload } from 'jsonwebtoken';
import Toast, { ToastProps } from 'libs/ui/src/lib/toast/toast';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { useSession } from '@/features/auth/hooks';
import client from '@/utils/client';

export default function Login() {
  const { status } = useSession();
  const router = useRouter();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [isShowPass, setShowPass] = useState(false);

  const [notification, setNotification] = useState<ToastProps>({
    type: 'success',
    title: '',
    description: '',
    open: false,
    setOpen(open) {},
  });

  const loginHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data } = await client.post('/auth/login', form);
      const user = jwt.decode(data.token) as JwtPayload;
      const payload = {
        user: {
          ...user,
          token: data.token,
        },
      };
      Cookies.set('token', JSON.stringify(payload), { expires: 7 });

      if (user.role === 'superadmin') {
        router.push('/dashboard/super-admin');
      } else {
        router.push('/dashboard/chat');
      }
    } catch (error) {
      setNotification({
        open: true,
        type: 'error',
        title: 'Wrong credentials. Try Again',
        setOpen(open) {},
      });
    }

    setLoading(false);

    setTimeout(() => {
      setNotification({ open: false, setOpen(open) {} });
    }, 3000);
  };

  if (status !== 'unauthenticated') {
    router.push('/dashboard/chat');
    return null;
  }

  return (
    <div className="h-[100vh]">
      <Toast
        type={notification.type}
        title={notification.title}
        description={notification.description}
        setOpen={notification.setOpen}
        open={notification.open}
      />
      <div className="flex min-h-full">
        <div className="bg-primary-400 relative flex w-0 flex-1 flex-col items-center text-center text-white">
          <div className="z-30 my-auto">
            <img
              className="mx-auto object-cover"
              src="/ilustrations/Frame 48096039.png"
              alt=""
            />
            <h2 className="mt-10 text-4xl font-bold tracking-tight">Dialox</h2>
            <p className="mx-auto mt-6 max-w-xl">
              Escalating your service more professional in a single dashboard
              and help you achieve a good customer experience that brings a good
              impact for your business.
            </p>
          </div>
          <div className="absolute -z-0">
            <img src="/vector-3.png" alt="" className="h-[100vh] w-[100vh]" />
          </div>
        </div>
        <div className="flex flex-1 items-center">
          <div className="mx-auto w-full max-w-lg">
            <div className="text-center">
              <h2 className="mt-6 text-5xl font-bold tracking-tight text-neutral-800">
                Login account
              </h2>
              <p className="mx-auto mt-5 max-w-sm text-sm text-gray-600">
                Welcome to Dialox, please put your login credentials below to
                start using the Dialox
              </p>
            </div>

            <div className="mt-8">
              <div className="mt-6">
                <form onSubmit={loginHandler} className="space-y-6">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      Email
                    </label>
                    <div className="relative mt-2">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <EnvelopeIcon
                          className="h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      </div>
                      <input
                        placeholder="Input email"
                        name="email"
                        type="email"
                        required
                        className="focus:ring-primary-400 block w-full rounded-md border-0 py-1.5 pl-10 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
                        value={form.email}
                        onChange={(e) =>
                          setForm({ ...form, email: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      Password
                    </label>
                    <div className="relative mt-2">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <LockClosedIcon
                          className="h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      </div>
                      <input
                        placeholder="Input password"
                        name="password"
                        type={isShowPass ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        className="focus:ring-primary-400 block w-full rounded-md border-0 py-1.5 pl-10 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
                        value={form.password}
                        onChange={(e) =>
                          setForm({ ...form, password: e.target.value })
                        }
                      />
                      <div
                        onClick={() => setShowPass(!isShowPass)}
                        className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3"
                      >
                        {isShowPass && (
                          <EyeSlashIcon className="h-5 w-5 text-gray-700" />
                        )}
                        {!isShowPass && (
                          <EyeIcon
                            className="h-5 w-5 text-gray-700"
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <Link
                        href="/forgot-password"
                        className="text-primary-400 hover:text-primary-400 font-bold"
                      >
                        Forgot your password?
                      </Link>
                    </div>
                  </div>

                  <div>
                    <button
                      disabled={loading}
                      type="submit"
                      className="bg-primary-400 focus-visible:outline-primary-400 disabled:bg-primary-300 flex w-full justify-center rounded-md px-3 py-2.5 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed"
                    >
                      Log In
                    </button>
                    <p className="mt-4 text-center text-sm text-neutral-700">
                      You don’t have an account?{' '}
                      <Link
                        href="/register"
                        className="text-primary-400 font-bold"
                      >
                        Register
                      </Link>
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
